import type {HTMLAttributes, MouseEventHandler, ReactNode} from 'react'
import {GripVertical} from 'lucide-react'
import {cn} from '@/lib/utils'
import {Icon} from '@/components/shared/Icon'

interface DragHandleProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onClick'> {
    onClick?: MouseEventHandler<HTMLDivElement>
    /** Render the handle faded with a not-allowed cursor (hover affordance removed). */
    disabled?: boolean
    children?: ReactNode
    className?: string
    'data-testid'?: string
}

/**
 * Small drag-handle hotspot for dnd-kit sortable rows. Spread the
 * useSortable attributes + listeners onto it:
 *
 * ```tsx
 * <DragHandle {...attributes} {...listeners} data-testid="drag-row-42"/>
 * ```
 *
 * Renders a {@link GripVertical} icon by default; pass children to override.
 */
export function DragHandle({children, className, disabled, ...rest}: DragHandleProps) {
    return (
        <div
            className={cn(
                'touch-none p-0.5 rounded',
                disabled
                    ? 'opacity-40 cursor-not-allowed'
                    : 'cursor-grab active:cursor-grabbing hover:bg-muted',
                className,
            )}
            {...rest}
        >
            {children ?? <Icon icon={GripVertical} size="xs" muted/>}
        </div>
    )
}
